import { SectionShell, Panel, Note } from "@/components/dash/primitives";
import { ChartTip, CHART, teu, num } from "@/components/dash/chartkit";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList,
} from "recharts";

export default function Lane({ data, num: sn, title }) {
  const fe = data.teu_labels.map((l, i) => ({ label: l, value: data.teu_far_east[i] }));
  const eu = data.teu_labels.map((l, i) => ({ label: l, value: data.teu_europe[i] }));

  return (
    <SectionShell id="lane" num={sn} title={title}>
      <p className="text-slate-700 leading-relaxed max-w-4xl mb-8">{data.intro}</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Panel
          title="Far East → Australasia/Oceania"
          subtitle="Monthly liftings (TEU)"
          source="Source: Alphaliner Monthly Monitor, Sep 2026 — CTS liftings"
          testid="chart-teu-fareast"
          csv={{ name: "Far East TEU", columns: ["Month", "TEU"], rows: fe.map((d) => [d.label, d.value]) }}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={fe} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} />
              <YAxis tickFormatter={(v) => `${v / 1000}k`} tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} width={44} />
              <Tooltip cursor={{ fill: "#f8fafc" }} content={<ChartTip formatter={(v) => `${num(v)} TEU`} />} />
              <Bar dataKey="value" name="Far East" fill={CHART.blue} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel
          title="Europe → Australasia/Oceania"
          subtitle="Monthly liftings (TEU) — note the smaller scale"
          source="Source: Alphaliner Monthly Monitor, Sep 2026 — CTS liftings"
          testid="chart-teu-europe"
          csv={{ name: "Europe TEU", columns: ["Month", "TEU"], rows: eu.map((d) => [d.label, d.value]) }}
        >
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={eu} margin={{ top: 8, right: 8, left: 8, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} />
              <YAxis tickFormatter={(v) => `${v / 1000}k`} tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} width={44} />
              <Tooltip cursor={{ fill: "#f8fafc" }} content={<ChartTip formatter={(v) => `${num(v)} TEU`} />} />
              <Bar dataKey="value" name="Europe" fill={CHART.slate} radius={[4, 4, 0, 0]} maxBarSize={48} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Note>{data.teu_note}</Note>

      <div className="mt-8">
        <Panel
          title="Year-on-year capacity growth by trade"
          subtitle="Cellular capacity deployed — Australasia highlighted"
          source="Source: Alphaliner Monthly Monitor, Sep 2026 — cellular capacity deployed"
          testid="chart-capacity-growth"
          csv={{ name: "Capacity growth by trade", columns: ["Trade", "YoY growth %"], rows: data.capacity_growth.map((d) => [d.trade, d.growth]) }}
        >
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.capacity_growth} layout="vertical" margin={{ top: 4, right: 40, left: 8, bottom: 4 }}>
              <CartesianGrid horizontal={false} stroke="#f1f5f9" />
              <XAxis type="number" tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={false} tick={{ fill: "#64748b" }} />
              <YAxis type="category" dataKey="trade" width={150} tickLine={false} axisLine={false} tick={{ fill: "#334155", fontSize: 12 }} />
              <Tooltip cursor={{ fill: "#f8fafc" }} content={<ChartTip formatter={(v) => `${v > 0 ? "+" : ""}${v}%`} />} />
              <Bar dataKey="growth" name="YoY capacity growth" radius={[0, 4, 4, 0]} maxBarSize={26}>
                {data.capacity_growth.map((d, i) => (
                  <Cell key={i} fill={d.growth < 0 ? CHART.red : d.highlight ? CHART.orange : CHART.blue} />
                ))}
                <LabelList dataKey="growth" position="right" formatter={(v) => `${v > 0 ? "+" : ""}${v}%`} className="font-num" fill="#334155" fontSize={11} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Note>{data.capacity_read}</Note>
    </SectionShell>
  );
}
